import type { DependsOnType } from '@unifyapps/defs/types/page';
import _get from 'lodash/get';
import _set from 'lodash/set';
import isEqual from 'react-fast-compare';
import { EMPTY_OBJECT } from '../../../../consts/empty';
import { isCompleteDependency } from '../../../dependency/const';
import type { ComputeContextType } from '../context/types';
import type { UseGetNoCodeComputeDataType } from '../useGetNoCodeComputedData';

interface PathMapping {
  path: string;
  isEntireEntity: boolean;
  id?: string;
}

interface Result {
  isContextChanged: boolean;
  nextContext: Record<string, unknown>;
}

const createExpressionKey = (path: string, depIndex: number, pathIndex: number): string => {
  return `path_${path.replace(/\./g, '_')}_${depIndex}_${pathIndex}`;
};

const createEntityKey = (id: string, depIndex: number): string => {
  return `entity_${id}_${depIndex}`;
};

export function buildExpressionMaps({
  dependsOn,
  context,
}: {
  dependsOn: DependsOnType[];
  context: ComputeContextType;
}) {
  const bulkExpressions: Record<string, string> = {};
  const expressionToPathMap: Record<string, PathMapping> = {};
  const nonStringValues: Record<string, unknown> = {};

  const dependsOnLength = dependsOn.length;
  for (let dependencyIndex = 0; dependencyIndex < dependsOnLength; dependencyIndex++) {
    const dependency = dependsOn[dependencyIndex];
    const { p: paths, id } = dependency;
    const pathsLength = paths.length;

    const isCompleteDependent = isCompleteDependency(id, paths);

    if (isCompleteDependent) {
      const currentValue = context[id];
      const expressionKey = createEntityKey(id, dependencyIndex);

      if (typeof currentValue === 'string') {
        bulkExpressions[expressionKey] = currentValue;
      } else {
        nonStringValues[expressionKey] = currentValue;
      }
      expressionToPathMap[expressionKey] = { path: id, isEntireEntity: true, id };
    } else {
      for (let pathIndex = 0; pathIndex < pathsLength; pathIndex++) {
        const path = paths[pathIndex];
        const currentValue = _get(context, path);
        const expressionKey = createExpressionKey(path, dependencyIndex, pathIndex);

        if (typeof currentValue === 'string') {
          bulkExpressions[expressionKey] = currentValue;
        } else {
          nonStringValues[expressionKey] = currentValue;
        }
        expressionToPathMap[expressionKey] = { path, isEntireEntity: false };
      }
    }
  }

  return {
    bulkExpressions,
    expressionToPathMap,
    nonStringValues,
  };
}

export function getIsDependentContextChanged({
  prevContext,
  context,
  dependsOn,
  notifyingUpdateForId,
  getNoCodeBulkComputedData,
}: {
  dependsOn: DependsOnType[];
  context: ComputeContextType;
  prevContext?: Record<string, unknown>;
  notifyingUpdateForId: string | null;
  getNoCodeBulkComputedData: UseGetNoCodeComputeDataType['getNoCodeBulkComputedData'];
}): Result {
  if (!prevContext) {
    return { isContextChanged: true, nextContext: EMPTY_OBJECT as Record<string, unknown> };
  }

  if (notifyingUpdateForId && prevContext[notifyingUpdateForId] === context[notifyingUpdateForId]) {
    return { isContextChanged: false, nextContext: prevContext };
  }

  const nextContext: Record<string, unknown> = {};

  const { bulkExpressions, expressionToPathMap, nonStringValues } = buildExpressionMaps({
    dependsOn,
    context,
  });

  let evaluatedResults: Record<string, unknown> = {};
  const bulkExpressionKeys = Object.keys(bulkExpressions);
  const bulkKeysLength = bulkExpressionKeys.length;

  if (bulkKeysLength > 0) {
    evaluatedResults = getNoCodeBulkComputedData(bulkExpressions, context);
  }

  const expressionKeys = Object.keys(expressionToPathMap);
  const expressionKeysLength = expressionKeys.length;

  for (let i = 0; i < expressionKeysLength; i++) {
    const expressionKey = expressionKeys[i];
    const mapping = expressionToPathMap[expressionKey];
    const { path, isEntireEntity, id } = mapping;

    let value: unknown;
    if (expressionKey in evaluatedResults) {
      value = evaluatedResults[expressionKey];
    } else if (expressionKey in nonStringValues) {
      value = nonStringValues[expressionKey];
    } else {
      continue;
    }

    if (isEntireEntity && id) {
      nextContext[id] = value;
    } else {
      _set(nextContext, path, value);
    }
  }

  const hasChanges =
    notifyingUpdateForId === null
      ? !isEqual(prevContext, nextContext)
      : !isEqual(prevContext[notifyingUpdateForId], nextContext[notifyingUpdateForId]);

  return {
    isContextChanged: hasChanges,
    nextContext,
  };
}
